import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { POLL_INTERVAL_MS, remaining, split, launchState, pad2 } from '@theme/eme-countdown-core';

const instances = new Set();
let ticker = null;

function tickAll() {
  const now = Date.now();
  for (const instance of instances) instance.update(now);
}

function watch(instance) {
  instances.add(instance);
  ticker ??= setInterval(tickAll, 1000);
}

function unwatch(instance) {
  instances.delete(instance);
  if (instances.size || ticker === null) return;
  clearInterval(ticker);
  ticker = null;
}

class EmeCountdown extends Component {
  #pollTimer = null;
  #updateQueued = false;
  #lastLiveRequestMs = -Infinity;

  connectedCallback() {
    super.connectedCallback();
    this.update(Date.now());
  }

  static get observedAttributes() {
    return ['data-state', 'data-available'];
  }

  attributeChangedCallback(_name, oldValue, newValue) {
    if (oldValue === null || oldValue === newValue || this.#updateQueued) return;
    this.#updateQueued = true;
    queueMicrotask(() => {
      this.#updateQueued = false;
      if (this.isConnected) this.update(Date.now());
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    unwatch(this);
    this.#stopPolling();
  }

  get #targetMs() {
    return Number(this.dataset.target);
  }

  get #available() {
    if (this.dataset.available === undefined) return undefined;
    return this.dataset.available === 'true';
  }

  update(nowMs) {
    const state = launchState({ targetMs: this.#targetMs, nowMs, available: this.#available });
    this.#render(state === 'upcoming' ? remaining(this.#targetMs, nowMs).total : 0);
    this.#enter(state);
    if (state === 'upcoming' || state === 'opening') watch(this);
    else unwatch(this);
  }

  #render(totalMs) {
    const parts = split(totalMs, this.dataset.units ?? 'hms');
    for (const unit of ['days', 'hours', 'minutes', 'seconds']) {
      const ref = this.refs[unit];
      if (ref instanceof HTMLElement) ref.textContent = pad2(parts[unit]);
    }
  }

  #enter(state) {
    if (state === 'opening') this.#startPolling();
    if (this.dataset.state === state) return;
    this.dataset.state = state;
    if (state === 'upcoming') {
      this.#stopPolling();
      this.refs.units?.removeAttribute('hidden');
      this.refs.opening?.setAttribute('hidden', '');
    }
    if (state === 'opening') {
      this.refs.units?.setAttribute('hidden', '');
      this.refs.opening?.removeAttribute('hidden');
    }
    if (state === 'live') {
      this.#stopPolling();
      this.#goLive();
    }
    if (state === 'ended') {
      this.#stopPolling();
      if (this.hasAttribute('data-ended-message')) this.removeAttribute('aria-label');
    }
  }

  #startPolling() {
    if (this.dataset.poll !== 'true' || this.#pollTimer || !this.dataset.handle) return;
    this.#pollTimer = setInterval(() => this.#checkAvailability(), POLL_INTERVAL_MS);
  }

  #stopPolling() {
    clearInterval(this.#pollTimer);
    this.#pollTimer = null;
  }

  async #checkAvailability() {
    if (document.hidden) return;
    try {
      const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}products/${this.dataset.handle}.js`);
      if (!response.ok) return;
      const product = await response.json();
      if (!product.available) return;
      this.dataset.available = 'true';
      this.#enter('live');
    } catch {
      return;
    }
  }

  async #goLive() {
    if (this.dataset.gate !== 'true' || !this.dataset.sectionId) return;
    const now = Date.now();
    if (now - this.#lastLiveRequestMs < POLL_INTERVAL_MS) return;
    this.#lastLiveRequestMs = now;
    await sectionRenderer.renderSection(this.dataset.sectionId, { cache: false });
  }
}

if (!customElements.get('eme-countdown')) {
  customElements.define('eme-countdown', EmeCountdown);
}
